import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { safeAnalytics } from '../../utils/analytics';
import { ANALYTICS_EVENTS } from '../../constants/analytics';
import { useWorkspaceRole } from '../../hooks/user/useWorkspaceRole';
import type { BirdChatHandover, BirdChatNavState } from '../../services/events/birdChatEvents';
import {
  birdChatNavStateForHandover,
  subscribeBirdChatOpen,
} from '../../services/events/birdChatEvents';
import { ROUTES } from '../../constants/routes';
import {
  BIRD_PANEL_MAX_VIEWPORT_FRACTION,
  BIRD_PANEL_MIN_WIDTH,
  PANEL_DEFAULT_WIDTH,
} from './nav/navRailWidth';
import { readPanelWidth } from '../../hooks/ui/usePanelResize';

interface UseBirdPanelOptions {
  isMobile: boolean;
  /** Desktop (lg+). Below it, Bower is fullscreen and closes on navigation. */
  isLargeScreen: boolean;
  /** True when the exclusive left-panel slot currently holds Bower. */
  isBirdOpen: boolean;
  /** Put Bower in the slot. Whatever held it is displaced structurally. */
  onOpenBird: () => void;
  /** Vacate the slot. */
  onCloseBird: () => void;
  /** Shared navigation tracker owned by useSidePanelState (its only writer). */
  prevPathname: string;
}

/**
 * Bower chat panel MECHANICS: the handoff payloads from other surfaces, the panel
 * width mirror, the drag flag, and the `chat:openPanel` entry point every
 * dispatcher uses.
 *
 * It deliberately owns no open/closed state and no exclusion rules. Whether Bower
 * is on screen is one value in the left-panel slot (`useLeftPanel`), which also
 * persists it; this hook is handed that answer plus the two transitions.
 */
export function useBirdPanel({
  isMobile,
  isLargeScreen,
  isBirdOpen,
  onOpenBird,
  onCloseBird,
  prevPathname,
}: UseBirdPanelOptions) {
  const navigate = useNavigate();
  const location = useLocation();
  const workspaceRole = useWorkspaceRole();

  // The prompt handed off by whichever surface opened the panel — pre-fill or
  // send, plus whether it wants a fresh conversation (see birdChatEvents.ts).
  // Files/context ride the in-memory CustomEvent — never serialised. Cleared
  // once the panel has applied it, so reopening the panel later doesn't replay
  // it against a conversation the user has since moved on from.
  const [birdHandover, setBirdHandover] = useState<BirdChatHandover | null>(null);
  const handleHandoverConsumed = useCallback(() => setBirdHandover(null), []);

  // Bower panel width state. This is the value the CONTAINER paints on first
  // frame, before the panel mounts and reports, so it must resolve the stored
  // width EXACTLY as BirdChatPanel's own seed does — same reader, same key,
  // same bounds. A disagreement is not a one-frame jump: the panel only reports
  // again when it is dragged or the viewport resizes, so a container that
  // resolved the stored value differently keeps the page margin wrong for the
  // whole session.
  const [birdPanelWidth, setBirdPanelWidth] = useState(() =>
    readPanelWidth(
      'birdPanelWidth',
      BIRD_PANEL_MIN_WIDTH,
      window.innerWidth * BIRD_PANEL_MAX_VIEWPORT_FRACTION,
      PANEL_DEFAULT_WIDTH,
    ),
  );

  // Mirror the panel's own width so the container tracks it. Persistence lives
  // in BirdChatPanel (written once when a drag ends) — writing localStorage from
  // here fired a synchronous write on every frame of a drag.
  const handleBirdWidthChange = useCallback((width: number) => {
    setBirdPanelWidth(width);
  }, []);

  // No live entry point in the desktop Bower panel. Live is a hands-free,
  // camera-in-hand mode, so it ships on the mobile Bower page only for now —
  // the composer renders its Live button solely where `onLiveTap` is wired,
  // and desktop deliberately doesn't wire it. The onboarding checklist sends
  // desktop users to their phone with a QR instead.

  // Open-Bower requests from anywhere in the app. Tracking is centralized HERE so
  // every entry point (dashboard CTA, onboarding checklist, the nav rail's Chat
  // button, future entry points) is covered automatically — dispatchers call
  // `openBirdChat({ source, … })` and we record the source dimension.
  //
  // Subscribed through `subscribeBirdChatOpen` rather than a hand-rolled
  // `addEventListener`: the subscriber is where the prompt-implies-fresh default
  // is resolved, so a raw dispatch still gets it. Hand-declaring the detail shape
  // here instead is the CR-003 drift shape — it compiles, both suites pass, and a
  // field added to the contract (this is exactly how `freshContext` arrived)
  // silently never reaches the panel.
  useEffect(() => {
    return subscribeBirdChatOpen(({ source, handover }) => {
      safeAnalytics.track(ANALYTICS_EVENTS.BIRD_CHAT_OPENED, {
        source,
        platform: isMobile ? 'mobile_web' : 'web',
        // Metadata (a role label), not PII — no egress-manifest entry
        // needed (rules/egress-policy.md gates UGC_CONTENT/IDENTIFIER, not
        // this). Lets dashboards measure guest chat adoption, the cost
        // case the owner accepted read-only Bower chat against. `null`
        // while the role is still resolving.
        workspace_role: workspaceRole ? workspaceRole.toLowerCase() : null,
      });

      // On mobile, Bower is a full-page route — navigate there with the handover
      // in router state instead of toggling a panel. A send payload degrades to
      // an editable pre-fill on mobile; serializable entity contexts and the
      // fresh-context decision carry over, while File objects do not.
      if (isMobile) {
        // Typed against the shared contract so a future field can't be added
        // here and silently never read on the other side of the cast.
        const navState: BirdChatNavState = birdChatNavStateForHandover(handover);
        navigate(ROUTES.mobileBird, { state: navState });
        return;
      }

      // A bare open (no prompt) still REPLACES the handover — with one whose
      // freshContext is false, so the panel resumes the existing conversation
      // rather than re-applying a previous surface's prompt.
      setBirdHandover(handover);

      onOpenBird();
    });
  }, [isMobile, navigate, onOpenBird, workspaceRole]);

  // Close bird panel on navigation when in fullscreen mode (small screens)
  useEffect(() => {
    if (location.pathname !== prevPathname && isBirdOpen && !isLargeScreen) {
      onCloseBird();
    }
  }, [location.pathname, prevPathname, isBirdOpen, isLargeScreen, onCloseBird]);

  return {
    birdHandover,
    handleHandoverConsumed,
    birdPanelWidth,
    handleBirdWidthChange,
  };
}
